(() => {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  const ADD_PATH_PATTERN = /\/cart\/add(?:\.js)?\/?$/;
  const REQUEST_TIMEOUT = 12000;

  class UCCartDrawerController {
    constructor() {
      this.opener = null;
      this.scrollY = 0;
      this.inertState = new Map();
      this.quantityTimers = new Map();
      this.externalAddTimer = null;
      this.pendingExternalAddUrl = null;
      this.upsellRequest = null;
      this.sectionRequest = null;
      this.refreshRevision = 0;
      this.isPageLocked = false;
      this.backgroundIsInert = false;
      this.pendingAddOpener = null;
      this.closeTimer = null;
      this.isClosing = false;

      this.onAddClickCapture = this.onAddClickCapture.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onCancel = this.onCancel.bind(this);

      document.addEventListener('click', this.onAddClickCapture, true);
      document.addEventListener('click', this.onClick);
      document.addEventListener('change', this.onChange);
      document.addEventListener('submit', this.onSubmit);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('cancel', this.onCancel, true);

      this.installNetworkObservers();
      this.syncHeaderCounts();
    }

    get dialog() {
      return document.querySelector('[data-uc-cart-drawer]');
    }

    get section() {
      return document.getElementById('shopify-section-cart-drawer');
    }

    get rootUrl() {
      return this.dialog?.dataset.ucCartRoot || window.Shopify?.routes?.root || '/';
    }

    get isOpen() {
      return Boolean(this.dialog?.hasAttribute('open'));
    }

    cartPayload(extra = {}) {
      const dialog = this.dialog;
      return {
        item_count: Number(dialog?.dataset.ucCartCount || 0),
        total_price: Number(dialog?.dataset.ucCartTotal || 0),
        currency: dialog?.dataset.ucCartCurrency || '',
        ...extra,
      };
    }

    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { detail: this.cartPayload(detail) }));
    }

    async open(opener = null, options = {}) {
      const dialog = this.dialog;
      if (!dialog) return;

      window.clearTimeout(this.closeTimer);
      this.isClosing = false;
      dialog.classList.remove('is-closing');
      const wasOpen = this.isOpen;
      if (opener) this.opener = opener;
      this.showDialog({ focus: !wasOpen });

      if (!wasOpen) {
        this.emit('uc:cart:open', { source: options.source || 'cart_icon' });
      }

      if (options.refresh !== false) {
        try {
          await this.refreshCartUI({ keepOpen: true, focus: false });
        } catch (error) {
          if (error?.name === 'AbortError') return;
          this.debugRenderError(error, 'open');
          this.showError(this.errorMessage(error));
        }
      } else if (options.loadRecommendations !== false) {
        await this.loadUpsell();
      }
    }

    showDialog({ focus = false } = {}) {
      const dialog = this.dialog;
      if (!dialog) return;

      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }

      this.lockPage();
      this.setBackgroundInert(true);

      if (focus) {
        window.requestAnimationFrame(() => {
          this.dialog?.querySelector('[data-uc-cart-close]')?.focus({ preventScroll: true });
        });
      }
    }

    close() {
      const dialog = this.dialog;
      if (!dialog?.open || this.isClosing) return;

      this.isClosing = true;
      const finalize = () => {
        if (!this.isClosing) return;
        window.clearTimeout(this.closeTimer);
        if (typeof dialog.close === 'function' && dialog.open) dialog.close();
        else dialog.removeAttribute('open');
        dialog.classList.remove('is-closing');
        this.isClosing = false;
        this.setBackgroundInert(false);
        this.unlockPage();

        const opener = this.opener;
        this.opener = null;
        const focusTarget = this.focusReturnTarget(opener);
        focusTarget?.focus({ preventScroll: true });
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finalize();
        return;
      }

      dialog.classList.add('is-closing');
      dialog.addEventListener('animationend', finalize, { once: true });
      this.closeTimer = window.setTimeout(finalize, 220);
    }

    isUsableFocusTarget(element) {
      if (!element?.isConnected || element.disabled || element.closest('[inert], [aria-hidden="true"]')) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    }

    focusReturnTarget(opener) {
      if (this.isUsableFocusTarget(opener)) return opener;

      const productId = opener?.dataset?.buyButton;
      if (productId) {
        const mainButton = [...document.querySelectorAll('[data-buy-button]')].find((button) => (
          button !== opener
          && button.dataset.buyButton === productId
          && !button.matches('[data-uc-sticky-buy]')
          && this.isUsableFocusTarget(button)
        ));
        if (mainButton) return mainButton;
      }

      return [...document.querySelectorAll('[data-uc-cart-open], a[href*="/cart"]')]
        .find((element) => this.isUsableFocusTarget(element)) || null;
    }

    lockPage() {
      if (this.isPageLocked) return;
      this.isPageLocked = true;
      this.scrollY = window.scrollY;
      document.body.style.top = `-${this.scrollY}px`;
      document.body.classList.add('uc-cart-locked');
    }

    unlockPage() {
      if (!this.isPageLocked) return;
      this.isPageLocked = false;
      document.body.classList.remove('uc-cart-locked');
      document.body.style.top = '';
      window.scrollTo({ top: this.scrollY, left: 0, behavior: 'auto' });
    }

    setBackgroundInert(makeInert) {
      const protectedNode = this.section;
      if (!protectedNode) return;

      if (makeInert) {
        if (this.backgroundIsInert) return;
        this.backgroundIsInert = true;
        this.inertState.clear();
        Array.from(document.body.children).forEach((element) => {
          if (element === protectedNode || ['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)) return;
          this.inertState.set(element, element.inert);
          element.inert = true;
        });
        return;
      }

      if (!this.backgroundIsInert) return;
      this.backgroundIsInert = false;
      this.inertState.forEach((wasInert, element) => {
        if (element.isConnected) element.inert = wasInert;
      });
      this.inertState.clear();
    }

    onAddClickCapture(event) {
      const addOpener = event.target.closest?.(
        'form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"], [data-add-to-cart], [data-buy-button], .product__atc button',
      );
      if (addOpener) this.pendingAddOpener = addOpener;
    }

    onClick(event) {
      const explicitOpener = event.target.closest('[data-uc-cart-open]');
      const cartLink = event.target.closest('a[href]');
      const opener = explicitOpener || (this.isCartLink(cartLink) ? cartLink : null);
      if (opener) {
        event.preventDefault();
        this.open(opener, { source: opener.dataset.ucCartOpen || 'cart_icon' });
        return;
      }

      if (event.target.closest('[data-uc-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest('[data-uc-cart-continue]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const dialog = this.dialog;
      if (dialog && event.target === dialog) {
        this.close();
        return;
      }

      const quantityButton = event.target.closest('[data-uc-cart-quantity-action]');
      if (quantityButton) {
        event.preventDefault();
        const line = quantityButton.closest('[data-uc-cart-line]');
        const input = line?.querySelector('[data-uc-cart-quantity]');
        if (!line || !input || quantityButton.disabled) return;
        const delta = quantityButton.dataset.ucCartQuantityAction === 'increase' ? 1 : -1;
        const nextQuantity = Math.max(0, Number(input.value || 0) + delta);
        input.value = String(nextQuantity);
        this.scheduleLineChange(line.dataset.lineKey, nextQuantity);
        return;
      }

      const removeButton = event.target.closest('[data-uc-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        const line = removeButton.closest('[data-uc-cart-line]');
        if (line) this.changeLine(line.dataset.lineKey, 0);
        return;
      }

      const upsellButton = event.target.closest('[data-uc-upsell-add]');
      if (upsellButton) {
        event.preventDefault();
        this.addUpsell(upsellButton);
        return;
      }

      const discountRemove = event.target.closest('[data-uc-cart-discount-remove]');
      if (discountRemove) {
        event.preventDefault();
        this.removeDiscount();
        return;
      }

      const checkoutButton = event.target.closest('[data-uc-cart-checkout]');
      if (checkoutButton) {
        this.emit('uc:cart:checkout', { source: 'cart_drawer' });
      }
    }

    onChange(event) {
      const quantityInput = event.target.closest('[data-uc-cart-quantity]');
      if (quantityInput) {
        const line = quantityInput.closest('[data-uc-cart-line]');
        const quantity = Math.max(0, Math.floor(Number(quantityInput.value || 0)));
        quantityInput.value = String(quantity);
        if (line) this.scheduleLineChange(line.dataset.lineKey, quantity, 0);
        return;
      }

      const variantSelect = event.target.closest('select[data-uc-upsell-variant]');
      if (variantSelect) {
        const card = variantSelect.closest('[data-uc-upsell-card]');
        const button = card?.querySelector('[data-uc-upsell-add]');
        const hasSelection = Boolean(variantSelect.value);
        if (button) {
          button.disabled = !hasSelection;
          button.textContent = 'Ajouter au panier';
        }
      }
    }

    onSubmit(event) {
      const discountForm = event.target.closest('[data-uc-cart-discount-form]');
      if (discountForm) {
        event.preventDefault();
        this.applyDiscount(discountForm);
        return;
      }

      const addForm = event.target.closest('form[action*="/cart/add"]');
      if (addForm) this.pendingAddOpener = event.submitter || document.activeElement;
    }

    onCancel(event) {
      if (!event.target.matches?.('[data-uc-cart-drawer]')) return;
      event.preventDefault();
      this.close();
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = this.dialog;
      const focusable = Array.from(dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      )).filter((element) => !element.hidden && element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    scheduleLineChange(lineKey, quantity, delay = 220) {
      if (!lineKey) return;
      window.clearTimeout(this.quantityTimers.get(lineKey));
      const timer = window.setTimeout(() => {
        this.quantityTimers.delete(lineKey);
        this.changeLine(lineKey, quantity);
      }, delay);
      this.quantityTimers.set(lineKey, timer);
    }

    async changeLine(lineKey, quantity) {
      const line = this.findLine(lineKey);
      this.setLineBusy(line, true);
      this.clearError();

      try {
        await this.requestJson(`${this.rootUrl}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity }),
        });
        if (!await this.refreshOrNavigate({ keepOpen: true, focus: false }, 'quantity')) return;
        const eventName = quantity === 0 ? 'uc:cart:remove' : 'uc:cart:change';
        this.emit(eventName, { line_key: lineKey, quantity, source: 'cart_drawer' });
        this.announce(quantity === 0 ? 'Produit supprimé du panier.' : 'Quantité mise à jour.');

        if (quantity > 0) {
          this.findLine(lineKey)?.querySelector('[data-uc-cart-quantity]')?.focus({ preventScroll: true });
        } else {
          this.dialog?.querySelector('[data-uc-cart-close]')?.focus({ preventScroll: true });
        }
      } catch (error) {
        this.setLineBusy(line, false);
        this.showError(this.errorMessage(error));
        this.announce(this.errorMessage(error));
      }
    }

    findLine(lineKey) {
      return Array.from(this.dialog?.querySelectorAll('[data-uc-cart-line]') || [])
        .find((line) => line.dataset.lineKey === lineKey);
    }

    setLineBusy(line, busy) {
      if (!line) return;
      line.setAttribute('aria-busy', String(busy));
      line.querySelectorAll('button, input').forEach((control) => {
        control.disabled = busy;
      });
    }

    async refreshCartUI({ keepOpen = this.isOpen, focus = false } = {}) {
      const section = this.section;
      if (!section) throw new Error('Le composant panier est introuvable.');

      const discount = this.dialog?.querySelector('[data-uc-cart-discount]');
      const discountWasOpen = Boolean(discount?.open);
      const discountValue = discount?.querySelector('input[name="discount"]')?.value || '';
      const rootUrl = this.rootUrl;
      const revision = ++this.refreshRevision;

      if (this.sectionRequest) this.sectionRequest.abort();
      const controller = new AbortController();
      this.sectionRequest = controller;
      section.setAttribute('aria-busy', 'true');

      try {
        let cart;
        let nextDialog;

        for (let attempt = 0; attempt < 2; attempt += 1) {
          const cacheKey = `${Date.now()}-${revision}-${attempt}`;
          cart = await this.requestJson(`${rootUrl}cart.js?uc_cart_sync=${cacheKey}`, {
            method: 'GET',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            cache: 'no-store',
            signal: controller.signal,
          });
          this.assertCurrentRefresh(revision, controller);

          const response = await this.fetchWithTimeout(`${rootUrl}cart?section_id=${SECTION_ID}&uc_cart_sync=${cacheKey}`, {
            headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
            cache: 'no-store',
            signal: controller.signal,
          });
          if (!response.ok) throw await this.responseError(response);

          const sectionHtml = await response.text();
          this.assertCurrentRefresh(revision, controller);
          const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
          nextDialog = parsed.querySelector('[data-uc-cart-drawer]');
          if (!nextDialog) throw this.cartRenderError('Le rendu actualisé du panier est indisponible.');

          const renderedCount = Number(nextDialog.dataset.ucCartCount || 0);
          if (renderedCount === Number(cart.item_count || 0)) break;
          nextDialog = null;
        }

        if (!nextDialog) throw this.cartRenderError('Le panier Shopify a changé pendant son actualisation.');
        this.applyCartSnapshot(nextDialog, cart);
        this.assertCurrentRefresh(revision, controller);
        section.innerHTML = nextDialog.outerHTML;
        this.syncHeaderCounts(cart.item_count);

        if (keepOpen) this.showDialog({ focus });
        const nextDiscount = this.dialog?.querySelector('[data-uc-cart-discount]');
        if (discountWasOpen && nextDiscount) nextDiscount.open = true;
        const nextDiscountInput = nextDiscount?.querySelector('input[name="discount"]');
        if (nextDiscountInput && discountValue) nextDiscountInput.value = discountValue;
        await this.loadUpsell(String(cart.items?.[0]?.product_id || ''), revision);
        return { cart, dialog: this.dialog };
      } finally {
        if (this.sectionRequest === controller) {
          section.removeAttribute('aria-busy');
          this.sectionRequest = null;
        }
      }
    }

    assertCurrentRefresh(revision, controller) {
      if (revision === this.refreshRevision && !controller.signal.aborted) return;
      const error = new Error('Cette actualisation du panier a été remplacée par une version plus récente.');
      error.name = 'AbortError';
      throw error;
    }

    cartRenderError(message) {
      const error = new Error(message);
      error.name = 'CartRenderError';
      return error;
    }

    applyCartSnapshot(dialog, cart) {
      const count = Math.max(0, Number(cart.item_count || 0));
      dialog.dataset.ucCartCount = String(count);
      dialog.dataset.ucCartTotal = String(Number(cart.total_price || 0));
      dialog.dataset.ucCartCurrency = String(cart.currency || dialog.dataset.ucCartCurrency || '');
      dialog.dataset.ucCartReferenceProductId = String(cart.items?.[0]?.product_id || '');
      dialog.dataset.ucCartProductIds = (cart.items || []).map((item) => item.product_id).join(',');

      const titleCount = dialog.querySelector('[data-uc-cart-title-count]');
      const titleLabel = dialog.querySelector('[data-uc-cart-title-label]');
      if (titleCount) titleCount.textContent = String(count);
      if (titleLabel) titleLabel.textContent = count > 1 ? 'articles' : 'article';
    }

    async refreshOrNavigate(options, source) {
      try {
        await this.refreshCartUI(options);
        return true;
      } catch (error) {
        if (error?.name === 'AbortError') return false;
        this.debugRenderError(error, source);
        this.showError(this.errorMessage(error));
        this.announce(this.errorMessage(error));
        return false;
      }
    }

    debugRenderError(error, source) {
      if (window.console?.warn) {
        window.console.warn(`[UC cart] Actualisation impossible (${source}).`, error);
      }
    }

    syncHeaderCounts(itemCount = Number(this.dialog?.dataset.ucCartCount || 0)) {
      const count = Math.max(0, Number(itemCount || 0));
      document.querySelectorAll('[data-uc-cart-count-badge], .cart-count, .uc-mobile-header__count').forEach((element) => {
        element.textContent = count > 0 ? String(count) : '';
        element.hidden = count === 0;
        element.classList.toggle('has-items', count > 0);
      });
      document.querySelectorAll('[data-uc-cart-open]').forEach((element) => {
        element.classList.toggle('has-items', count > 0);
        element.setAttribute('aria-label', count > 0
          ? `Panier : ${count} article${count > 1 ? 's' : ''}`
          : 'Panier vide');
      });
    }

    async applyDiscount(form) {
      const details = form.closest('[data-uc-cart-discount]');
      const input = form.querySelector('input[name="discount"]');
      const button = form.querySelector('button[type="submit"]');
      const code = input?.value.trim();
      if (!code) {
        this.setDiscountResult('Saisissez un code de réduction.', 'error');
        input?.focus();
        return;
      }

      button.disabled = true;
      if (details) details.open = true;
      form.setAttribute('aria-busy', 'true');
      this.setDiscountResult('Vérification du code…', 'loading');

      try {
        const cart = await this.requestJson(`${this.rootUrl}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: code }),
        });

        const normalizedCode = code.toLocaleLowerCase();
        const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
        const applications = [
          ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
          ...cart.items.flatMap((item) => item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application),
        ];
        const confirmed = discountCodes.some((discount) =>
          String(discount.code || '').toLocaleLowerCase() === normalizedCode && discount.applicable !== false,
        ) || applications.some((application) =>
          String(application?.title || '').toLocaleLowerCase() === normalizedCode,
        );

        if (!await this.refreshOrNavigate({ keepOpen: true, focus: false }, 'discount')) return;
        if (!confirmed) {
          this.setDiscountResult('Ce code n’est pas applicable à ce panier. Le panier n’a pas été modifié.', 'error');
          return;
        }

        this.setDiscountResult('Code appliqué et confirmé par Shopify.', 'success');
        this.announce('Code de réduction appliqué.');
      } catch (error) {
        this.setDiscountResult(this.errorMessage(error), 'error');
      } finally {
        const currentForm = this.dialog?.querySelector('[data-uc-cart-discount-form]');
        currentForm?.removeAttribute('aria-busy');
        const currentButton = currentForm?.querySelector('button[type="submit"]');
        if (currentButton) currentButton.disabled = false;
      }
    }

    setDiscountResult(message, state) {
      const result = this.dialog?.querySelector('[data-uc-cart-discount-result]');
      if (!result) return;
      result.textContent = message;
      result.dataset.state = state;
    }

    async removeDiscount() {
      this.clearError();
      try {
        await this.requestJson(`${this.rootUrl}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: '' }),
        });
        if (!await this.refreshOrNavigate({ keepOpen: true, focus: false }, 'discount_remove')) return;
        const details = this.dialog?.querySelector('[data-uc-cart-discount]');
        if (details) details.open = true;
        this.setDiscountResult('Code de réduction retiré.', 'success');
        this.announce('Code de réduction retiré.');
      } catch (error) {
        this.showError(this.errorMessage(error));
      }
    }

    async loadUpsell(referenceProductId = this.dialog?.dataset.ucCartReferenceProductId, refreshRevision = this.refreshRevision) {
      if (this.upsellRequest) this.upsellRequest.abort();
      const container = this.dialog?.querySelector('[data-uc-cart-upsell]');
      if (!container || !referenceProductId) return;
      if (container.dataset.loadedFor === referenceProductId) return;

      const controller = new AbortController();
      this.upsellRequest = controller;
      container.hidden = true;

      try {
        let recommendation = await this.fetchRecommendation(referenceProductId, 'complementary', controller.signal);
        if (!recommendation?.querySelector('[data-uc-upsell-card]')) {
          recommendation = await this.fetchRecommendation(referenceProductId, 'related', controller.signal);
        }
        const currentContainer = this.dialog?.querySelector('[data-uc-cart-upsell]');
        const isCurrent = this.upsellRequest === controller
          && refreshRevision === this.refreshRevision
          && this.dialog?.dataset.ucCartReferenceProductId === referenceProductId
          && currentContainer === container;
        if (!isCurrent) return;

        if (!recommendation?.querySelector('[data-uc-upsell-card]')) {
          container.innerHTML = '';
          container.hidden = true;
          container.dataset.loadedFor = referenceProductId;
          return;
        }

        container.innerHTML = recommendation.innerHTML;
        container.hidden = false;
        container.dataset.loadedFor = referenceProductId;
      } catch (error) {
        if (error.name !== 'AbortError' && this.upsellRequest === controller) container.hidden = true;
      } finally {
        if (this.upsellRequest === controller) this.upsellRequest = null;
      }
    }

    async fetchRecommendation(productId, intent, signal) {
      const params = new URLSearchParams({
        product_id: productId,
        section_id: SECTION_ID,
        intent,
        limit: '6',
      });
      const response = await this.fetchWithTimeout(`${this.rootUrl}recommendations/products?${params}`, {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        signal,
      });
      if (!response.ok) return null;
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-uc-cart-upsell]');
    }

    async addUpsell(button) {
      const card = button.closest('[data-uc-upsell-card]');
      const variantControl = card?.querySelector('[data-uc-upsell-variant]');
      const variantId = Number(variantControl?.value || 0);
      if (!variantId) {
        this.showError('Choisissez une taille ou une variante avant l’ajout.');
        variantControl?.focus();
        return;
      }

      button.disabled = true;
      card.setAttribute('aria-busy', 'true');
      this.clearError();

      try {
        await this.requestJson(`${this.rootUrl}cart/add.js?uc_source=upsell`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (!await this.refreshOrNavigate({ keepOpen: true, focus: false }, 'upsell')) return;
        const detail = {
          product_id: Number(card.dataset.productId || 0),
          variant_id: variantId,
          quantity: 1,
          source: 'cart_drawer_upsell',
        };
        this.emit('uc:cart:add', detail);
        this.emit('uc:cart:upsell_add', detail);
        this.announce('✓ Produit complémentaire ajouté');
      } catch (error) {
        button.disabled = false;
        card.removeAttribute('aria-busy');
        this.showError(this.errorMessage(error));
      }
    }

    handleExternalAdd(url) {
      this.pendingExternalAddUrl = url;
      window.clearTimeout(this.externalAddTimer);
      this.externalAddTimer = window.setTimeout(async () => {
        try {
          const requestUrl = this.pendingExternalAddUrl;
          this.pendingExternalAddUrl = null;
          const wasOpen = this.isOpen;
          if (!await this.refreshOrNavigate({ keepOpen: wasOpen, focus: false }, 'external_add')) return;
          const opener = this.pendingAddOpener;
          this.pendingAddOpener = null;
          await this.open(opener, { refresh: false, loadRecommendations: false, source: 'confirmed_add' });
          this.emit('uc:cart:add', { source: 'theme_add_to_cart', request_url: new URL(requestUrl, location.origin).pathname });
          this.announce('✓ Produit ajouté');
        } catch (error) {
          this.showError(this.errorMessage(error));
        }
      }, 80);
    }

    installNetworkObservers() {
      if (window.__ucCartNetworkObserved) return;
      window.__ucCartNetworkObserved = true;

      const originalFetch = window.fetch.bind(window);
      window.fetch = async (...args) => {
        const response = await originalFetch(...args);
        try {
          const input = args[0];
          const init = args[1] || {};
          const url = typeof input === 'string' || input instanceof URL ? String(input) : input.url;
          const method = String(init.method || input?.method || 'GET').toUpperCase();
          if (method === 'POST' && response.ok && this.shouldObserveAdd(url)) this.handleExternalAdd(url);
        } catch (_) {
          // Ne jamais altérer la réponse du code appelant.
        }
        return response;
      };

      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function(method, url, ...rest) {
        this.__ucCartRequest = { method: String(method).toUpperCase(), url: String(url) };
        return originalOpen.call(this, method, url, ...rest);
      };
      XMLHttpRequest.prototype.send = function(...args) {
        this.addEventListener('loadend', () => {
          const request = this.__ucCartRequest;
          const controller = window.UCCartDrawer;
          if (request?.method === 'POST' && this.status >= 200 && this.status < 300 && controller?.shouldObserveAdd(request.url)) {
            controller.handleExternalAdd(request.url);
          }
        }, { once: true });
        return originalSend.apply(this, args);
      };
    }

    shouldObserveAdd(url) {
      const parsed = new URL(url, window.location.origin);
      if (!ADD_PATH_PATTERN.test(parsed.pathname)) return false;
      if (parsed.searchParams.has('opus')) return false;
      return !['upsell', 'direct_checkout', 'personalization'].includes(parsed.searchParams.get('uc_source'));
    }

    isCartLink(link) {
      if (!link || window.location.pathname.replace(/\/+$/, '') === `${this.rootUrl}cart`.replace(/\/+$/, '')) return false;
      try {
        const url = new URL(link.href, window.location.origin);
        if (url.origin !== window.location.origin) return false;
        return url.pathname.replace(/\/+$/, '') === `${this.rootUrl}cart`.replace(/\/+$/, '');
      } catch (_) {
        return false;
      }
    }

    async requestJson(url, options) {
      const response = await this.fetchWithTimeout(url, options);
      const raw = await response.text();
      let payload;
      try {
        payload = JSON.parse(raw);
      } catch (_) {
        const error = new Error(response.ok ? 'Réponse Shopify invalide. Réessayez.' : `Erreur Shopify ${response.status}.`);
        error.status = response.status;
        throw error;
      }
      if (!response.ok) {
        const error = new Error(payload.description || payload.message || 'La demande Shopify a échoué.');
        error.status = response.status;
        throw error;
      }
      return payload;
    }

    async fetchWithTimeout(url, options = {}) {
      const timeoutController = new AbortController();
      const timeout = window.setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT);
      const externalSignal = options.signal;
      const abortFromExternal = () => timeoutController.abort();
      if (externalSignal?.aborted) timeoutController.abort();
      else externalSignal?.addEventListener('abort', abortFromExternal, { once: true });

      try {
        return await window.fetch(url, { ...options, signal: timeoutController.signal });
      } catch (error) {
        if (error.name === 'AbortError' && !externalSignal?.aborted) {
          const timeoutError = new Error('La requête a expiré. Vérifiez votre connexion et réessayez.');
          timeoutError.name = 'TimeoutError';
          throw timeoutError;
        }
        throw error;
      } finally {
        window.clearTimeout(timeout);
        externalSignal?.removeEventListener('abort', abortFromExternal);
      }
    }

    async responseError(response) {
      try {
        const payload = await response.json();
        const error = new Error(payload.description || payload.message || `Erreur Shopify ${response.status}.`);
        error.status = response.status;
        return error;
      } catch (_) {
        const error = new Error(`Erreur Shopify ${response.status}.`);
        error.status = response.status;
        return error;
      }
    }

    errorMessage(error) {
      if (error?.status === 422) return error.message || 'Stock insuffisant ou quantité indisponible.';
      if (error?.name === 'AbortError') return 'La requête a été interrompue.';
      if (error?.name === 'CartRenderError') {
        return 'Le panier n’a pas pu être actualisé. Rechargez la page puis réessayez.';
      }
      return error?.message || 'Une erreur réseau est survenue. Réessayez.';
    }

    showError(message) {
      const element = this.dialog?.querySelector('[data-uc-cart-error]');
      if (!element) return;
      const safeMessage = /Shopify n[’']a pas renvoyé|rendu Shopify inattendu|nouveau rendu du panier/i.test(String(message || ''))
        ? 'Le panier n’a pas pu être actualisé. Rechargez la page puis réessayez.'
        : message;
      element.textContent = safeMessage;
      element.hidden = false;
    }

    clearError() {
      const element = this.dialog?.querySelector('[data-uc-cart-error]');
      if (!element) return;
      element.textContent = '';
      element.hidden = true;
    }

    announce(message) {
      const element = this.dialog?.querySelector('[data-uc-cart-status]');
      if (!element) return;
      element.textContent = '';
      window.setTimeout(() => { element.textContent = message; }, 20);
    }
  }

  function initialize() {
    const dialog = document.querySelector('[data-uc-cart-drawer]');
    if (!dialog || dialog.dataset.ucCartEnabled !== 'true') return;
    window.UCCartDrawer = new UCCartDrawerController();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
